import { services } from '@config/services';
import { runInChunks } from '@/lib/fetchAllPages';
import { vendorApi } from './_base';

/**
 * Vendor product catalog endpoints (`/v1/vendorProducts*`). The
 * merchant-override layer over these is in
 * `./merchantVendorProducts.ts`.
 *
 * `getVendorProducts` returns the FULL vendor catalog with per-facility
 * specialization rows; `updateFacilityProduct` patches one row.
 */

/** Vendor-product status enum (matches backend). */
export type VendorProductStatus = 'Available' | 'OutOfStock' | 'Archived' | string;

/** Money-shaped value for product cost. */
export interface MoneyAmount {
  amount?: number;
  currency?: string;
}

/** Per-facility specialization of a vendor product. */
export interface VendorProductFacilityRow {
  id: string;
  vendorSku?: string;
  vendorSkuDescription?: string;
  decorations?: unknown;
  productCost?: MoneyAmount;
  productionTimeDays?: number;
  status?: VendorProductStatus;
}

/** One vendor product row (with per-facility info). */
export interface VendorProductRow {
  id: string;
  sku?: string;
  variantId?: string;
  /** Catalog product the variant belongs to, derived server-side on write.
   * Comes back `null` on rows written before that derivation existed. */
  productId?: string | null;
  /** Mapping status: true when the product carries the OrderMesh data points
   * (sku + variantId). Unmapped products are routable only via plugin mappings. */
  isMapped?: boolean;
  facilities: VendorProductFacilityRow[];
}

export interface VendorProductsResponse {
  vendorProducts: VendorProductRow[];
  total: number;
  paginationToken?: string | null;
}

export interface VendorProductsArgs {
  vendorId: string;
  facilityId?: string | null;
  /** Multi-facility filter (union with `facilityId` server-side). */
  facilityIds?: string[];
  searchString?: string | null;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  showBy?: string;
  useCursor?: boolean;
  paginationToken?: string | null;
  /** Mapping-status filter: true = mapped only, false = unmapped only, omitted = all. */
  isMapped?: boolean;
}

/** Payload for `updateFacilityProduct` (per-facility specialization update). */
export interface FacilityProductUpdate {
  status?: VendorProductStatus;
  vendorSku?: string;
  vendorSkuDescription?: string;
  productCost?: MoneyAmount;
  productionTimeDays?: number;
  [key: string]: unknown;
}

/** One per-id outcome from the bulk DELETE. */
export interface VendorProductsBulkResult {
  id: string;
  status: number;
}

/** Aggregated result of a (possibly batched) bulk delete. `errored` is true if
 * any batch failed at the transport level — the caller then suppresses the
 * success toast and reports an interrupted/failed delete. */
export interface DeleteVendorProductsResponse {
  bulk: VendorProductsBulkResult[];
  errored: boolean;
}

export interface DeleteVendorProductsArgs {
  vendorId: string;
  ids: string[];
  /** Ids per DELETE request (legacy: 250). */
  batchSize?: number;
  /** Concurrent in-flight batches (legacy: 4). */
  concurrency?: number;
}

/** Args for the criteria delete: every product of a vendor, minus `keepIds`. */
export interface DeleteAllVendorProductsForVendorArgs {
  vendorId: string;
  /** Ids to spare from deletion (the "select all then deselect a few" case). */
  keepIds: string[];
}

/** Result of the criteria delete — count of soft-deleted products. */
export interface DeleteAllVendorProductsForVendorResponse {
  deletedCount: number;
}

/**
 * One product in an add/update bulk payload — the output shape of
 * `convertJsonDataToApiFormat`. Each item carries its OWN `vendorId` (the bulk
 * body has no top-level vendorId, unlike delete), so the batch builder posts
 * `{ bulk }` only — verified rank-1 from the legacy service + live.
 */
export interface VendorProductBulkItem {
  id?: string;
  variantId?: string;
  sku?: string;
  vendorId?: string;
  facilities?: unknown[];
  [key: string]: unknown;
}

/** Per-item outcome of an add/update bulk op (status + optional error detail). */
export interface VendorProductsBulkOpResult {
  status?: number;
  response?: { errors?: { errorMessage?: string }[] };
  [key: string]: unknown;
}

export interface VendorProductsBulkResponse {
  bulk: VendorProductsBulkOpResult[];
}

/**
 * Pure request builders — the wire-shape-lock targets.
 * - delete: body `{ bulk, vendorId }` (ids + top-level vendorId), `DELETE`.
 * - add:    body `{ bulk }` (items carry their own vendorId), `POST`.
 * - update: body `{ bulk }`, `PUT`.
 * All hit `/vendor/v1/vendorProducts/bulk`. Verbatim from the legacy
 * `vendorServices.{deleteVendorProducts,addProductsToVendor,updateVendorProducts}`.
 */
export const vendorProductsRequestBuilders = {
  deleteVendorProductsBatch: (vendorId: string, ids: string[]) => ({
    url: `${services.vendor}/v1/vendorProducts/bulk`,
    method: 'DELETE' as const,
    body: { bulk: ids, vendorId },
  }),
  addVendorProductsBatch: (bulk: VendorProductBulkItem[]) => ({
    url: `${services.vendor}/v1/vendorProducts/bulk`,
    method: 'POST' as const,
    body: { bulk },
  }),
  updateVendorProductsBatch: (bulk: VendorProductBulkItem[]) => ({
    url: `${services.vendor}/v1/vendorProducts/bulk`,
    method: 'PUT' as const,
    body: { bulk },
  }),
  deleteAllVendorProductsForVendor: (vendorId: string, keepIds: string[]) => ({
    url: `${services.vendor}/v1/vendorProducts/all`,
    method: 'DELETE' as const,
    body: { vendorId, keepIds },
  }),
};

/**
 * Normalize one batch's response into per-id `{ id, status }`. The endpoint may
 * return `{ bulk: [...] }` OR an empty / non-JSON body on full success (the
 * legacy service synthesized all-200 there). Any 2xx maps to 200; an entry with
 * `error` and no status maps to 500; a missing id is backfilled positionally.
 */
export function normalizeBulkDeleteBody(
  raw: unknown,
  requestedIds: string[],
): VendorProductsBulkResult[] {
  const bulk = (raw as { bulk?: unknown } | null | undefined)?.bulk;
  if (!Array.isArray(bulk)) {
    return requestedIds.map((id) => ({ id, status: 200 }));
  }
  return bulk.map((entry, i) => {
    const e = (entry ?? {}) as { id?: string; status?: number; error?: unknown };
    const id = e.id ?? requestedIds[i] ?? '';
    const rawStatus = e.status ?? (e.error ? 500 : 200);
    const status = rawStatus >= 200 && rawStatus < 300 ? 200 : rawStatus;
    return { id, status };
  });
}

/**
 * The one place the `GET /v1/vendorProducts` wire contract is built — also
 * used by the simulator's per-vendor sample fan-out
 * (`vendorProductsForVendors.ts`), so a server-side param change lands once.
 *
 * Server-contract notes (verified against the legacy service call):
 * - `searchAll` is the search-string param name.
 * - `sortAsc` is the boolean direction (legacy convention).
 * - When `facilityId` is set, the response still includes the full
 *   product (with all its facilities); the consumer filters down.
 */
export function vendorProductsUrl({
  vendorId,
  facilityId,
  facilityIds,
  searchString,
  page = 1,
  pageSize = 25,
  sortBy,
  sortDir,
  showBy,
  useCursor,
  paginationToken,
  isMapped,
}: VendorProductsArgs): string {
  const qs = new URLSearchParams();
  qs.set('vendorId', vendorId);
  if (facilityId) qs.set('facilityId', facilityId);
  for (const id of facilityIds ?? []) qs.append('facilityIds', id);
  if (searchString) qs.set('searchAll', searchString);
  qs.set('page', String(page));
  qs.set('pageSize', String(pageSize));
  if (sortBy) qs.set('sortBy', sortBy);
  if (sortDir) qs.set('sortAsc', String(sortDir === 'asc'));
  if (showBy) qs.set('showBy', showBy);
  if (useCursor) qs.set('useCursor', 'true');
  if (paginationToken) qs.set('paginationToken', paginationToken);
  if (isMapped !== undefined) qs.set('isMapped', String(isMapped));
  return `${services.vendor}/v1/vendorProducts?${qs.toString()}`;
}

const vendorProductsApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /vendor/v1/vendorProducts — see `vendorProductsUrl` for the contract. */
    getVendorProducts: builder.query<VendorProductsResponse, VendorProductsArgs>({
      query: (args) => ({ url: vendorProductsUrl(args), method: 'GET' }),
      providesTags: (_r, _e, { vendorId }) => [
        { type: 'Vendor', id: `products:${vendorId}` },
      ],
    }),
    /**
     * PUT /vendor/v1/vendorProducts/{vendorProductId}/facilities/{facilityId}
     *
     * `vendorProductId` is the `vendor_products` ROW id (`VendorProductRow.id`)
     * — NOT `VendorProductRow.productId`, which is the catalog product the
     * variant belongs to. Passing the catalog id here targets a different
     * record or 404s, so the argument is named for the id it actually wants.
     */
    updateFacilityProduct: builder.mutation<
      void,
      {
        vendorId: string;
        vendorProductId: string;
        facilityId: string;
        data: FacilityProductUpdate;
      }
    >({
      query: ({ vendorProductId, facilityId, data }) => ({
        url: `${services.vendor}/v1/vendorProducts/${vendorProductId}/facilities/${facilityId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_r, _e, { vendorId }) => [
        { type: 'Vendor', id: `products:${vendorId}` },
      ],
    }),
    /**
     * PATCH /vendor/v1/vendorProducts/{vendorProductId}/facilities/{facilityId}
     * — true partial update: omitted fields are left untouched server-side,
     * unlike `updateFacilityProduct` which replaces the whole facility row.
     * `vendorProductId` is the row id, same as the PUT above.
     */
    patchFacilityProduct: builder.mutation<
      void,
      {
        vendorId: string;
        vendorProductId: string;
        facilityId: string;
        data: FacilityProductUpdate;
      }
    >({
      query: ({ vendorProductId, facilityId, data }) => ({
        url: `${services.vendor}/v1/vendorProducts/${vendorProductId}/facilities/${facilityId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_r, _e, { vendorId }) => [
        { type: 'Vendor', id: `products:${vendorId}` },
      ],
    }),
    /**
     * Bulk-delete vendor products. Batches the id list (default 250/batch, 4
     * concurrent) over `DELETE /v1/vendorProducts/bulk` and aggregates the
     * per-id outcomes. Best-effort: every batch runs; a transport-failed batch
     * marks its ids failed (status 500) and sets `errored`.
     *
     * Deliberately does NOT `invalidatesTags`: the list is eventually-consistent
     * (Atlas Search reindex lags the write), so the caller POLLS the list with
     * backoff and shows the result only after it reflects the delete — see
     * `useBulkDeleteVendorProducts`. A single tag-invalidation
     * would refetch once into stale data, the exact bug this fix avoids.
     */
    deleteVendorProducts: builder.mutation<DeleteVendorProductsResponse, DeleteVendorProductsArgs>({
      async queryFn({ vendorId, ids, batchSize = 250, concurrency = 4 }, _api, _extra, fetchWithBQ) {
        const batches: string[][] = [];
        for (let i = 0; i < ids.length; i += batchSize) batches.push(ids.slice(i, i + batchSize));
        let errored = false;
        const perBatch = await runInChunks(
          batches,
          async (batch) => {
            const res = await fetchWithBQ(
              vendorProductsRequestBuilders.deleteVendorProductsBatch(vendorId, batch),
            );
            if (res.error) {
              errored = true;
              return batch.map((id) => ({ id, status: 500 }));
            }
            return normalizeBulkDeleteBody(res.data, batch);
          },
          { chunk: concurrency },
        );
        return { data: { bulk: perBatch.flat(), errored } };
      },
    }),

    /**
     * DELETE /vendor/v1/vendorProducts/all — criteria delete of a vendor's
     * whole catalog, minus `keepIds` (the "select all then deselect a few"
     * case). One server-side query by vendorId — no client-side id enumeration,
     * which is the whole point of this path. Gated backend-side by a feature
     * flag (OFF → 400). Returns `{ deletedCount }`. Like the batched delete it
     * does NOT `invalidatesTags`: the list is Atlas-Search-eventual, so the
     * caller polls (see `useBulkDeleteVendorProducts`).
     */
    deleteAllVendorProductsForVendor: builder.mutation<
      DeleteAllVendorProductsForVendorResponse,
      DeleteAllVendorProductsForVendorArgs
    >({
      query: ({ vendorId, keepIds }) =>
        vendorProductsRequestBuilders.deleteAllVendorProductsForVendor(vendorId, keepIds),
    }),

    /**
     * POST /vendor/v1/vendorProducts/bulk — add ONE batch of products.
     * Body `{ bulk }`; each item carries its own vendorId. The orchestrating
     * hook (`useBulkUploadVendorProducts`) chunks the full payload (100/batch,
     * 6 concurrent, network-retry) and ticks the progress modal — mirroring the
     * legacy saga `runBatchedBulk`/`batchedBulkWorker`. No `invalidatesTags`:
     * the list is Atlas-Search-eventual, so the caller polls (add) or refetches
     * (update) explicitly, same as delete.
     */
    addVendorProductsBatch: builder.mutation<VendorProductsBulkResponse, VendorProductBulkItem[]>({
      query: (bulk) => vendorProductsRequestBuilders.addVendorProductsBatch(bulk),
    }),

    /** PUT /vendor/v1/vendorProducts/bulk — update ONE batch (see add). */
    updateVendorProductsBatch: builder.mutation<VendorProductsBulkResponse, VendorProductBulkItem[]>({
      query: (bulk) => vendorProductsRequestBuilders.updateVendorProductsBatch(bulk),
    }),

    /**
     * GET /vendor/swagger/v1/swagger.json — the OpenAPI spec backing the
     * client-side XLSX validation (extractValidationRules reads model
     * constraints from it). Long-cached; degrades gracefully — if this fails
     * the upload flows skip client validation and let the backend validate,
     * exactly as the legacy did on a swagger fetch error.
     */
    getVendorSwagger: builder.query<unknown, void>({
      query: () => ({ url: `${services.vendor}/swagger/v1/swagger.json`, method: 'GET' }),
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const {
  useGetVendorProductsQuery,
  useLazyGetVendorProductsQuery,
  useUpdateFacilityProductMutation,
  usePatchFacilityProductMutation,
  useDeleteVendorProductsMutation,
  useDeleteAllVendorProductsForVendorMutation,
  useAddVendorProductsBatchMutation,
  useUpdateVendorProductsBatchMutation,
  useGetVendorSwaggerQuery,
  useLazyGetVendorSwaggerQuery,
} = vendorProductsApi;
